/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {StateDeclarationBuilder, AdnRouterHelperApi} from '../../routing/state-utils';
import {LayoutIncludeApiService, LayoutIncludeModel} from './layout-include-api.service';
import {LayoutIncludeCopyModalComponent} from './layout-include-copy-modal.component';
import {LayoutIncludeEditPageComponent} from './layout-include-edit-page.component';
import {LayoutIncludeRoutePageComponent} from './layout-include-route-page.component';

export function layoutIncludeStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(LayoutIncludeApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const readOnly = () => !permissions.hasNetwork(PERMISSIONS.manageLayouts.apiKey);
  const rh = adnRouterHelper.createRouterHelper(
    'layout-includes.layout-include',
    LayoutIncludeApiService,
    'app.adm',
    'app/admin'
  );
  const copyConfig = {
    modalComponent: LayoutIncludeCopyModalComponent,
    stateUrl: 'app.adm.layout-includes.layout-include',
    lcase: 'layout include',
    ucase: 'Layout Include'
  };

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: LayoutIncludeRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      layoutIncludePage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: LayoutIncludeModel = {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []};
          return new SignalAdminResourcePageState(model, true, readOnly(), api, 'Layout Include', 'name', copyConfig);
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Layout Include', 'name', copyConfig));
      },
      model: function(layoutIncludePage: SignalAdminResourcePageState<LayoutIncludeModel>) {
        return layoutIncludePage.model();
      }
    },
    views: {
      'content@app': {component: LayoutIncludeEditPageComponent, bindings: {state: 'layoutIncludePage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'layoutIncludePage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'layoutIncludePage'}}
    }
  }));
  return states.build();
}
